import React, { createContext, useContext, useEffect } from 'react'
import { TrackStrategyRegistry } from './track-strategy-registry'
import { AnalyticsTracker } from './analytics-tracker'
import {
  EVENT_NAME_PREFIX,
  TrackClickStrategy,
  TrackImpressionStrategy,
} from './track-strategy'
import { TrackEventQueue } from './track-event-queue'
import { PageRegistry } from './page-registry'
import { EventNameBuilder } from './event-name-builder'
import logUtil from '../../../utils/log-util'

const pageRegistry = new PageRegistry()

pageRegistry.register('/dashboard', 'home')
pageRegistry.register('/dashboard/applications/sandbox', 'app-credentials')
pageRegistry.register('/dashboard/applications/live', 'app-credentials')
pageRegistry.register('/dashboard/upgrade-your-apps', 'upgrade-your-apps')
pageRegistry.register(
  '/dashboard/upgrade-your-apps/upgrade-guide',
  'upgrade-guide',
)

const eventNameBuilder = new EventNameBuilder()
eventNameBuilder.prefix(EVENT_NAME_PREFIX)

const tracker = new AnalyticsTracker()
const trackRegistry = new TrackStrategyRegistry()
const trackEventQueue = new TrackEventQueue(tracker, trackRegistry)

trackRegistry.register(
  'pressed',
  new TrackClickStrategy(tracker, eventNameBuilder),
)
trackRegistry.register(
  'shown',
  new TrackImpressionStrategy(tracker, eventNameBuilder),
)
trackRegistry.register(
  'scrolled',
  new TrackImpressionStrategy(tracker, eventNameBuilder),
)

function getPage() {
  const pageLocation = window.location.pathname
    .split('/')
    .slice(0, 4)
    .join('/')
    .replace(/\/$/, '')
  const page = pageRegistry.retrieve(pageLocation)
  return page
}

function track(action, data) {
  const page = getPage()
  trackEventQueue.queue({ ...data, action, page })
}

function trackShown(data) {
  track('shown', data)
}

function trackPress(data) {
  track('pressed', data)
}

function trackScroll(data) {
  const documentElement = document.documentElement
  const documentBody = document.body

  const scrollDepth =
    ((documentElement.scrollTop || documentBody.scrollTop) /
      ((documentElement.scrollHeight || documentBody.scrollHeight) -
        documentElement.clientHeight)) *
    100

  const roundedScrollDepth = Math.round(scrollDepth / 25) * 25
  const clampedScrollDepth = Math.max(0, Math.min(100, roundedScrollDepth))

  track('scrolled', { ...data, scrollDepth: clampedScrollDepth })
}

function createTrackScrollAt(percentage) {
  return function trackScrollAt(onTrack) {
    const documentElement = document.documentElement
    const documentBody = document.body

    const scrollDepth =
      ((documentElement.scrollTop || documentBody.scrollTop) /
        ((documentElement.scrollHeight || documentBody.scrollHeight) -
          documentElement.clientHeight)) *
      100

    const roundedScrollDepth = Math.round(scrollDepth / 25) * 25
    const clampedScrollDepth = Math.max(0, Math.min(100, roundedScrollDepth))

    if (percentage <= clampedScrollDepth) {
      track('scrolled', { scrollDepth: percentage })
      onTrack()
    }
  }
}

const AnalyticsContext = createContext(null)
export const useAnalytics = () => useContext(AnalyticsContext)

export function useTrackShown(data) {
  const analytics = useAnalytics()

  useEffect(() => {
    analytics.trackShown({ ...data })
  }, [])
}

export function useTrackScrolled() {
  useEffect(() => {
    const listeners = []
    const scrollListeners = [
      createTrackScrollAt(25),
      createTrackScrollAt(50),
      createTrackScrollAt(75),
      createTrackScrollAt(100),
    ]

    scrollListeners.forEach((listener) => {
      function scrollendListener() {
        listener(() => {
          window.removeEventListener('scrollend', scrollendListener)
        })
      }

      listeners.push(scrollendListener)

      window.addEventListener('scrollend', scrollendListener)
    })

    return function () {
      listeners.forEach((listener) => {
        window.removeEventListener('scrollend', listener)
      })
    }
  }, [])
}

export function AnalyticsProvider({ children }) {
  useEffect(function unqueue() {
    const interval = setInterval(() => {
      try {
        trackEventQueue.unqueue()
      } catch (error) {
        logUtil.error(error)
      }
    }, 1000)

    return function () {
      clearInterval(interval)
    }
  })

  return (
    <AnalyticsContext.Provider
      value={{ trackPress, trackShown, trackScroll, getPage }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

export default AnalyticsProvider
